import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About"
        path="/about"
        description="Founded by Adam Slager, Slatech Solutions is a relationship-driven industrial solutions company serving Alberta and Texas — bridging customers, suppliers, manufacturers, and industry partners."
      />
      {/* PAGE HERO */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1717386255773-a456c611dc4e"
            alt="Industrial machinery"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-blue-500 mb-5">About Slatech</p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight text-white leading-[0.9] max-w-4xl">
              The best solutions are built on strong relationships
            </h1>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section data-testid="founder-story" className="bg-background py-20 md:py-28 bg-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="aspect-[4/5] overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1496247749665-49cf5b1022e9"
                alt="Industrial fabrication facility"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-6 border border-white/10 bg-zinc-950 p-6">
              <p className="font-heading text-2xl font-bold uppercase tracking-tight text-white">Adam Slager</p>
              <p className="text-blue-500 text-sm uppercase tracking-[0.2em] font-heading mt-1">Founder, Slatech Solutions</p>
            </div>
          </Reveal>

          <Reveal delay={150} className="lg:col-span-7">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-500 mb-5">Our Story</p>
            <div className="space-y-6 text-lg text-zinc-300 leading-relaxed">
              <p>
                Slatech Solutions was founded by <span className="text-white font-semibold">Adam Slager</span> after
                years working directly with customers across the industrial, manufacturing, mining, and energy sectors.
              </p>
              <p>Through those experiences, one lesson became clear:</p>
              <blockquote className="relative border-l-2 border-blue-600 pl-6 py-2">
                <Quote size={28} className="text-blue-600 mb-3" />
                <p className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white leading-tight">
                  The best solutions are built on strong relationships.
                </p>
              </blockquote>
              <p>
                Slatech Solutions exists to bridge the gap between customers, suppliers, manufacturers, and industry
                partners — by identifying opportunities and connecting the right people to solve complex challenges.
              </p>
              <p>
                We're a modern, agile company built on industry knowledge and strategic connections. Not decades of
                bureaucracy — just the right relationships, applied to the problem in front of you.
              </p>
              <p>
                With a footprint across <span className="text-white font-semibold">Alberta and Texas</span>, we connect
                customers to the right people and solutions on both sides of the border — from the Canadian energy
                corridor to the Gulf Coast industrial base.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-12">
              {[
                { k: "Relationship", v: "Driven" },
                { k: "Industry", v: "Knowledge" },
                { k: "Strategic", v: "Connections" },
              ].map((b) => (
                <div key={b.k} className="bg-zinc-950 p-6">
                  <p className="font-heading text-xl font-bold uppercase tracking-tight text-white">{b.k}</p>
                  <p className="text-blue-500 uppercase tracking-[0.2em] text-sm font-heading mt-1">{b.v}</p>
                </div>
              ))}
            </div>

            <Link
              to="/contact"
              data-testid="about-cta"
              className="mt-12 inline-flex items-center gap-2 font-heading text-lg font-semibold uppercase tracking-widest text-white hover:text-blue-500 transition-colors duration-200 group"
            >
              Start a conversation
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTORS */}
      <section className="bg-black border-t border-white/10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-500 mb-4">Sectors We Serve</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-3xl">
              Connections across heavy industry
            </h2>
          </Reveal>
          <div className="flex flex-wrap gap-3 mt-10">
            {["Oil & Gas", "Drilling", "Manufacturing", "Fabrication", "Mining", "Industrial", "Equipment", "Machine Tool"].map(
              (t) => (
                <span
                  key={t}
                  className="font-heading text-base uppercase tracking-widest text-zinc-200 border border-white/15 hover:border-blue-500 hover:text-white px-5 py-3 transition-colors duration-200"
                >
                  {t}
                </span>
              )
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
